import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { generatePDFDocumentFromRef } from "../../../util/generatePDF.tsx";
import Spinner from "../../../components/Spinner/Spinner.tsx";
import { submitContact } from "../../../api/contact";

interface RentalYieldInputs {
  propertyPrice: string;
  stampDuty: string;
  otherPurchasingCosts: string;
  rent: string;
  otherAdminCosts: string;
  growth: string;
  mortgage: string;
  serviceCharge: string;
  groundRent: string;
  managementFee: string;
}

interface CalculatedValues {
  totalPurchasingPrice: number;
  monthlyRent: number;
  totalPurchasingCosts: number;
  totalAnnualExpenses: number;
  grossYield: number;
  netYield: number;
  netYieldWithPurchasing: number;
  annualIncome: number;
  netMonthlyIncome: number;
  capitalGrowth: number;
  roi: number;
}

interface YieldPage2Props {}

export default function YieldPage2(props: YieldPage2Props): JSX.Element {
  const location = useLocation();
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [inputs, setInputs] = useState<RentalYieldInputs>({
    propertyPrice: "",
    stampDuty: "",
    otherPurchasingCosts: "",
    rent: "",
    otherAdminCosts: "",
    growth: "5",
    mortgage: "",
    serviceCharge: "",
    groundRent: "",
    managementFee: "",
  });

  const [calculated, setCalculated] = useState<CalculatedValues>({
    totalPurchasingPrice: 0,
    monthlyRent: 0,
    totalPurchasingCosts: 0,
    totalAnnualExpenses: 0,
    grossYield: 0,
    netYield: 0,
    netYieldWithPurchasing: 0,
    annualIncome: 0,
    netMonthlyIncome: 0,
    capitalGrowth: 0,
    roi: 0,
  });

  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const [isGrossYieldMode, setIsGrossYieldMode] = useState<boolean>(true);

  const LABEL_WIDTH = "w-56 min-w-[210px]"; // Increased width for left label alignment

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError(null);
    setContactSuccess(false);
    setContactSubmitting(true);

    try {
      await submitContact({
        name: contactName.trim(),
        email: contactEmail.trim(),
        phone: contactPhone.trim() || undefined,
        subject: "Yield Strategy Enquiry",
        message: contactMessage.trim(),
        sourcePage: location.pathname,
        enquiryType: "general",
      });

      setContactSuccess(true);
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setContactMessage("");
    } catch (err) {
      setContactError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleDownloadPDF = () => {
    setIsGeneratingPDF(true);
    setTimeout(async () => {
      if (tableRef.current) {
        try {
          const doc = await generatePDFDocumentFromRef(tableRef);
          if (doc) {
            doc.save("Rental Yield Analysis.pdf");
          }
        } catch (err) {
          // eslint-disable-next-line
          console.error(err);
        } finally {
          setIsGeneratingPDF(false);
        }
      }
    }, 100);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setInputs((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const calculateValues = (form: typeof inputs) => {
    // Numbers
    const propertyPrice = parseFloat(form.propertyPrice) || 0;
    const stampDuty = parseFloat(form.stampDuty) || 0;
    const otherPurchasingCosts = parseFloat(form.otherPurchasingCosts) || 0;
    const annualRent = (parseFloat(form.rent) || 0) * 12;
    const otherAdminCosts = parseFloat(form.otherAdminCosts) || 0;
    const mortgage = parseFloat(form.mortgage) || 0;
    const serviceCharge = parseFloat(form.serviceCharge) || 0;
    const groundRent = parseFloat(form.groundRent) || 0;
    const managementFee = parseFloat(form.managementFee) || 0;
    const growth = parseFloat(form.growth) || 0;

    const totalPurchasingCosts =
      propertyPrice + stampDuty + otherPurchasingCosts;

    // Expenses
    const managementFeeTotal = annualRent * (managementFee / 100);
    const totalAnnualExpenses =
      mortgage +
      serviceCharge +
      groundRent +
      managementFeeTotal +
      otherAdminCosts;

    const grossYield =
      propertyPrice > 0 ? (annualRent / propertyPrice) * 100 : 0;
    const netYield =
      propertyPrice > 0
        ? ((annualRent - totalAnnualExpenses) / propertyPrice) * 100
        : 0;
    const netYieldWithPurchasing =
      totalPurchasingCosts > 0
        ? ((annualRent - totalAnnualExpenses) / totalPurchasingCosts) * 100
        : 0;

    const netIncome = annualRent - totalAnnualExpenses;
    const netMonthlyIncome = netIncome / 12;

    const capitalGrowth = propertyPrice * (growth / 100);
    const roi =
      totalPurchasingCosts > 0
        ? ((netIncome + capitalGrowth) / totalPurchasingCosts) * 100
        : 0;

    return {
      totalPurchasingPrice: propertyPrice,
      monthlyRent: annualRent / 12,
      totalPurchasingCosts,
      totalAnnualExpenses,
      grossYield,
      netYield,
      netYieldWithPurchasing,
      annualIncome: annualRent,
      netMonthlyIncome,
      capitalGrowth,
      roi,
    };
  };

  // Remove submit button, calculate instantly as input changes
  useEffect(() => {
    setCalculated(calculateValues(inputs));
    // eslint-disable-next-line
  }, [inputs]);

  return (
    <>
      <Helmet>
        <title>Rental Yield Calculator | Letting Engine</title>
        <meta
          name="description"
          content="The definitive ROI engine for property investors. Calculate Gross and Net yield instantly. Identify the true performance of your asset before you buy or let."
        />
        <meta
          name="keywords"
          content="residential property letting agency, real estate, london, rental yield, return on investment, gross yield, net yield"
        />
      </Helmet>

      <section className="page-header py-32 pb-16 max-w-[1600px] mx-auto px-[5%]">
        <h1 className="font-teko text-7xl tracking-[-1px] mb-4 text-start">
          RENTAL YIELD
          <br />
          <span className="text-brand-yellow">CALCULATOR</span>
        </h1>
        <p className="font-manrope text-lg text-text-slate max-w-[700px] leading-relaxed mr-auto text-start">
          The definitive ROI engine for property investors. Calculate Gross and
          Net yield instantly. Identify the true performance of your asset
          before you buy or let.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] lg:gap-10 max-w-[1600px] mx-auto px-[5%] pb-32 ">
        {/* LEFT: CALCULATOR */}
        <div className="col-span-2 lg:col-span-1">
          <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] p-6 sm:p-16 relative overflow-hidden clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)] shadow-soft">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-start sm:items-end mb-12 border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-4">
              <h3 className="font-teko text-3xl text-text-obsidian">
                Yield Engine
              </h3>
              <div className="flex bg-bg-porcelain border border-solid border-[#ddd] p-1.5 transform ">
                <button
                  className={`py-2 px-6 font-teko text-lg uppercase cursor-pointer transition-all duration-300 transform ${
                    isGrossYieldMode
                      ? "bg-text-obsidian text-brand-yellow"
                      : "text-text-slate"
                  }`}
                  onClick={() => setIsGrossYieldMode(true)}
                  type="button"
                >
                  Gross Yield
                </button>
                <button
                  className={`py-2 px-6 font-teko text-lg uppercase cursor-pointer transition-all duration-300 transform ${
                    !isGrossYieldMode
                      ? "bg-text-obsidian text-brand-yellow"
                      : "text-text-slate"
                  }`}
                  onClick={() => setIsGrossYieldMode(false)}
                  type="button"
                >
                  Net Yield
                </button>
              </div>
            </div>

            {/* FORM, labels & values left aligned and vertically centered */}
            <form className="grid gap-8" autoComplete="off">
              <div className="grid gap-8 grid-cols-1">
                <div className=" flex items-center">
                  <label
                    htmlFor="propertyValue"
                    className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                  >
                    Property Value
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                    <span className="font-manrope text-2xl text-text-slate pr-2 bg-transparent">
                      £
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                      placeholder="0"
                      name="propertyPrice"
                      value={inputs.propertyPrice}
                      onChange={handleInputChange}
                      id="propertyValue"
                      min="0"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className=" flex items-center">
                  <label
                    htmlFor="stampDuty"
                    className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                  >
                    Stamp Duty
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                    <span className="font-manrope text-2xl text-text-slate pr-2 bg-transparent">
                      £
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                      placeholder="0"
                      name="stampDuty"
                      value={inputs.stampDuty}
                      onChange={handleInputChange}
                      id="stampDuty"
                      min="0"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className=" flex items-center">
                  <label
                    htmlFor="otherPurchasingCosts"
                    className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                  >
                    Other Purchasing Costs
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                    <span className="font-manrope text-2xl text-text-slate pr-2 bg-transparent">
                      £
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                      placeholder="0"
                      name="otherPurchasingCosts"
                      value={inputs.otherPurchasingCosts}
                      onChange={handleInputChange}
                      id="otherPurchasingCosts"
                      min="0"
                      autoComplete="off"
                    />
                  </div>
                </div>
                <div className=" flex items-center">
                  <label
                    htmlFor="monthlyRent"
                    className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                  >
                    Monthly Rent
                  </label>
                  <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                    <span className="font-manrope text-2xl text-text-slate pr-2 bg-transparent">
                      £
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                      placeholder="0"
                      name="rent"
                      value={inputs.rent}
                      onChange={handleInputChange}
                      id="monthlyRent"
                      min="0"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
              <div className=" flex items-center">
                <label
                  htmlFor="otherAdminCosts"
                  className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                >
                  Other Admin Costs{" "}
                  <span className="font-manrope text-xs normal-case ml-1 bg-transparent">
                    (annual)
                  </span>
                </label>
                <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                  <span className="font-manrope text-2xl text-text-slate pr-2 bg-transparent">
                    £
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                    placeholder="0"
                    name="otherAdminCosts"
                    value={inputs.otherAdminCosts}
                    onChange={handleInputChange}
                    id="otherAdminCosts"
                    min="0"
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className=" flex items-center">
                <label
                  htmlFor="growth"
                  className={`font-teko text-lg uppercase text-text-obsidian tracking-wide ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                >
                  Growth Estimate{" "}
                  <span className="font-manrope text-xs normal-case ml-1 bg-transparent">
                    (%)
                  </span>
                </label>
                <div className="flex items-center border-b-2 border-b-solid border-b-[#ddd] focus-within:border-brand-yellow transition-all duration-300 w-full ml-4">
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    className="w-full border-none p-4 text-xl font-manrope text-text-obsidian outline-none bg-transparent hideSpinner text-left [appearance:textfield]"
                    placeholder="%"
                    name="growth"
                    value={inputs.growth}
                    onChange={handleInputChange}
                    id="growth"
                    min="0"
                    autoComplete="off"
                  />
                </div>
              </div>
              {!isGrossYieldMode && (
                <div className="bg-bg-porcelain p-8 mt-4 border-l-2 border-l-solid border-l-brand-yellow">
                  <h4 className="font-teko text-xl mb-4 text-text-slate uppercase text-left">
                    Operational Costs (Annual)
                  </h4>
                  <div className="grid gap-6 grid-cols-1">
                    <div className=" flex items-center m-0">
                      <label
                        htmlFor="mortgage"
                        className={`font-teko text-base uppercase text-text-obsidian ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                      >
                        Mortgage
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*"
                        className="w-full p-3 text-left border border-solid border-[#ddd] bg-white font-manrope text-base hideSpinner [appearance:textfield]"
                        placeholder="£"
                        name="mortgage"
                        value={inputs.mortgage}
                        onChange={handleInputChange}
                        id="mortgage"
                        min="0"
                        autoComplete="off"
                      />
                    </div>
                    <div className=" flex items-center m-0">
                      <label
                        htmlFor="serviceCharge"
                        className={`font-teko text-base uppercase text-text-obsidian ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                      >
                        Service Charge
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*"
                        className="w-full p-3 text-left border border-solid border-[#ddd] bg-white font-manrope text-base hideSpinner [appearance:textfield]"
                        placeholder="£"
                        name="serviceCharge"
                        value={inputs.serviceCharge}
                        onChange={handleInputChange}
                        id="serviceCharge"
                        min="0"
                        autoComplete="off"
                      />
                    </div>
                    <div className=" flex items-center m-0">
                      <label
                        htmlFor="groundRent"
                        className={`font-teko text-base uppercase text-text-obsidian ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                      >
                        Ground Rent
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*"
                        className="w-full p-3 text-left border border-solid border-[#ddd] bg-white font-manrope text-base hideSpinner [appearance:textfield]"
                        placeholder="£"
                        name="groundRent"
                        value={inputs.groundRent}
                        onChange={handleInputChange}
                        id="groundRent"
                        min="0"
                        autoComplete="off"
                      />
                    </div>
                    <div className=" flex items-center m-0">
                      <label
                        htmlFor="managementFee"
                        className={`font-teko text-base uppercase text-text-obsidian ${LABEL_WIDTH} mb-0 flex-shrink-0 flex items-center`}
                      >
                        Management (%)
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*"
                        className="w-full p-3 text-left border border-solid border-[#ddd] bg-white font-manrope text-base hideSpinner [appearance:textfield]"
                        placeholder="%"
                        name="managementFee"
                        value={inputs.managementFee}
                        onChange={handleInputChange}
                        id="managementFee"
                        min="0"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* No Run Analysis Button */}
            </form>

            {/* Results Section: left aligned, bright but not saturated red/green */}
            <div
              ref={tableRef}
              className="bg-bg-porcelain text-text-slate rounded-lg p-12 mt-16 relative grid md:grid-cols-2 gap-6 grid-cols-1 shadow-soft text-left"
            >
              {/* GROSS YIELD */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Gross Yield
                </span>
                <div className="font-teko text-4xl text-[#ffe44c] leading-none mb-1">
                  {calculated.grossYield.toFixed(1)}%
                </div>
                <span className="font-manrope text-xs text-[#999]">
                  Based on Property Value
                </span>
              </div>

              {/* NET YIELD */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs text-[#ecd136] block mb-1">
                  Net Yield
                </span>
                <div className="font-teko text-4xl text-[#1d2939] leading-none mb-1">
                  {calculated.netYield.toFixed(1)}%
                </div>
                <span className="font-manrope text-xs text-[#999]">
                  After Expenses
                </span>
              </div>

              <div className="md:col-span-2 h-px bg-[rgba(0,0,0,0.06)] my-4 col-span-1"></div>

              {/* ANNUAL INCOME */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Annual Income
                </span>
                <div className="font-teko text-3xl text-text-obsidian leading-none mb-1">
                  £
                  {calculated.annualIncome.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              {/* TOTAL EXPENSES */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Total Expenses
                </span>
                <div className="font-teko text-3xl text-text-obsidian leading-none mb-1">
                  £
                  {calculated.totalAnnualExpenses.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              <div className="md:col-span-2 h-px bg-[rgba(0,0,0,0.06)] my-3 col-span-1"></div>

              {/* ESTIMATED ANNUAL GROWTH */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Estimated Annual Growth
                </span>
                <div className="font-teko text-2xl text-text-obsidian leading-none mb-1">
                  £
                  {calculated.capitalGrowth.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                  <span className="text-xs ml-1 text-[#888]">
                    ({inputs.growth || "0"}%)
                  </span>
                </div>
              </div>

              {/* NET MONTHLY INCOME */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Net Monthly Income
                </span>
                <div className="font-teko text-2xl text-text-obsidian leading-none mb-1">
                  £
                  {calculated.netMonthlyIncome.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>

              <div className="md:col-span-2 h-px bg-[rgba(0,0,0,0.06)] my-3 col-span-1"></div>

              {/* NET YIELD (INC PURCHASING) */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Net Yield (Inc. Purchasing)
                </span>
                <div className="font-teko text-xl text-text-obsidian leading-none mb-1">
                  {calculated.netYieldWithPurchasing.toFixed(1)}%
                </div>
                <span className="font-manrope text-xs text-[#888]">
                  Includes Stamp Duty & Costs
                </span>
              </div>
              {/* ROI */}
              <div className="relative flex flex-col items-start">
                <span className="font-manrope uppercase tracking-wide text-xs opacity-70 block mb-1">
                  Full ROI (Inc. Growth)
                </span>
                <div className="font-teko text-xl text-text-obsidian leading-none mb-1">
                  {calculated.roi.toFixed(1)}%
                </div>
                <span className="font-manrope text-xs text-[#888]">
                  Net Income + Growth
                </span>
              </div>
            </div>

            <button
              className="w-full p-4 mt-8 bg-brand-yellow text-text-obsidian font-teko text-xl uppercase tracking-wide cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,97%_100%,0_100%)] hover:translate-y-[-3px] hover:shadow-[0_15px_30px_rgba(255,196,0,0.18)]"
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              type="button"
            >
              {isGeneratingPDF ? (
                <>
                  <Spinner /> Downloading PDF...
                </>
              ) : (
                <>Download PDF</>
              )}
            </button>
          </div>
        </div>
        {/* RIGHT: STICKY FORM */}
        <div className="relative w-full lg:w-[400px] mt-20 lg:mt-0">
          <div className="sticky top-32">
            <div className="bg-[var(--glass-surface)] backdrop-blur-[25px] border-[var(--glass-border)] p-6 md:p-12 shadow-[0_30px_60px_rgba(0,0,0,0.05)] clip-path-[polygon(10%_0,100%_0,100%_100%,0%_100%,0_5%)]">
              <h3 className="font-teko text-3xl text-text-obsidian mb-2">
                Yield Strategy
              </h3>
              <p className="font-manrope text-sm text-text-slate leading-relaxed mb-8">
                Is your asset underperforming? Speak to our portfolio managers
                about strategies to increase yield through refurbishment or
                refinancing.
              </p>
              <form className="grid gap-5" onSubmit={handleContactSubmit}>
                {contactSuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
                    Thank you — your enquiry has been sent.
                  </p>
                )}
                {contactError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
                    {contactError}
                  </p>
                )}
                <input
                  type="text"
                  inputMode="text"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Full Name"
                  id="yieldStrategyFullNameInput"
                  autoComplete="off"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="email"
                  inputMode="email"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Email Address"
                  id="yieldStrategyEmailAddressInput"
                  autoComplete="off"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  required
                  disabled={contactSubmitting}
                />
                <input
                  type="tel"
                  inputMode="tel"
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  placeholder="Phone Number"
                  id="yieldStrategyPhoneNumberInput"
                  autoComplete="off"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  disabled={contactSubmitting}
                />
                <textarea
                  className="w-full p-4 bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] font-manrope transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_85%,98%_100%,0_100%)] focus:outline-none focus:border-brand-yellow"
                  rows={3}
                  placeholder="I want to discuss my portfolio..."
                  id="yieldStrategyMessageInput"
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  required
                  disabled={contactSubmitting}
                ></textarea>
                <button
                  type="submit"
                  className="w-full p-4 mt-4 bg-text-obsidian text-white border-none font-teko text-xl uppercase cursor-pointer transition-all duration-300 clip-path-[polygon(0_0,100%_0,100%_80%,95%_100%,0_100%)] hover:bg-[#c3b370] hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={contactSubmitting}
                >
                  {contactSubmitting ? "Sending…" : "Request Audit"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* CONTENT: INTELLIGENCE */}
      <section className="py-32 relative">
        <div className="max-w-[1440px] mx-auto px-[5%] content-block">
          <h2 className="font-teko text-6xl mb-12 text-text-obsidian">
            Yield Intelligence
          </h2>

          <h4 className="font-teko text-3xl mt-12 mb-4 text-text-obsidian pl-4 border-l-4 border-l-solid border-l-brand-yellow">
            Understanding the Metrics
          </h4>
          <p className="font-manrope text-base leading-relaxed text-text-slate mb-6">
            Rental yield is the primary metric for assessing the efficiency of a
            property investment. It represents the annual return on capital
            generated by the asset.
          </p>

          <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] mb-4 transition-all duration-300 hover:border-brand-yellow hover:translate-x-3">
            <h5 className="font-teko text-xl mb-2">Gross Yield</h5>
            <p className="font-manrope text-base m-0">
              (Annual Rental Income ÷ Property Value) × 100. This is a quick
              snapshot of the property's potential before costs.
            </p>
          </div>

          <div className="bg-bg-white p-8 border border-solid border-[rgba(0,0,0,0.05)] mb-4 transition-all duration-300 hover:border-brand-yellow hover:translate-x-3">
            <h5 className="font-teko text-xl mb-2">Net Yield</h5>
            <p className="font-manrope text-base m-0">
              (Annual Rent – Annual Costs) ÷ Property Value) × 100. This is the
              true performance metric, accounting for mortgage interest,
              maintenance, and fees.
            </p>
          </div>

          <h4 className="font-teko text-3xl mt-12 mb-4 text-text-obsidian pl-4 border-l-4 border-l-solid border-l-brand-yellow">
            What is a "Good" Yield?
          </h4>
          <p className="font-manrope text-base leading-relaxed text-text-slate mb-6">
            In the current London market, a Gross Yield between{" "}
            <strong className="font-semibold">4% and 6%</strong> is considered
            strong for residential assets. Yields above 7% are typically found
            in HMOs (Houses in Multiple Occupation) or emerging regeneration
            zones.
          </p>

          <h4 className="font-teko text-3xl mt-12 mb-4 text-text-obsidian pl-4 border-l-4 border-l-solid border-l-brand-yellow">
            Optimizing Your Return
          </h4>
          <p className="font-manrope text-base leading-relaxed text-text-slate mb-6">
            Maximizing yield isn't just about raising rent. It involves
            minimizing void periods through premium marketing, conducting
            strategic refurbishments to boost capital value, and optimizing tax
            efficiency.
          </p>
        </div>
      </section>
    </>
  );
}
